import React, { useEffect, useState } from 'react';
import { DEVICE_BLOCKER_Z_INDEX } from 'Constants/StackingLevels';
import { FC } from 'react';
import * as eventActions from 'Actions/events';
import { Box } from '@mui/material';
import Logo from 'Components/Logo';
import { useDispatch, useSelector } from 'react-redux';
import { ALLOWED_PATHS_ANY_PLATFORM, isCapacitorNative } from 'Utils/Constants';
import { AppDispatch } from 'Reducers';
import { detectDevice } from 'Utils';
import { isFirefoxBrowser, isSafariBrowser } from 'Utils/UserAgent';
import { useLocation } from 'react-router';
import { MUSEFLOW_OFF_WHITE_1 } from 'Components/Colors';
import LogOutButton from 'Components/LogOutButton';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { MainAppReducer } from 'Types';
import {
  shouldShowSafariCompatibilityBlocker,
  useExplicitPreferredInputMode,
} from 'Utils/PreferredInputMode';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { getDeviceClass } from 'Utils/Responsive';
import { useBlockingGateHoldsBack } from 'Hooks/useHardwareBack';
import {
  ANDROID_PLAY_STORE_URL,
  IOS_APP_STORE_URL,
  isAuthOrOnboardingPath,
  openAppThenStore,
  shouldShowMobileWebBlocker,
  storeTargetForDevice,
  storeUrlForTarget,
  type StoreTarget,
} from './deviceCompatibilityBlocking.logic';

// Show note that they need to open their browser that is not safari!
const DeviceCompatibilityBlockingScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();
  const auth = useAuthWrapperContext();
  const { userDataFetched } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const [showSafariBlockingScreen, setShowSafariBlockingScreen] = useState(false);
  const [showMobileBlockingScreen, setShowMobileBlockingScreen] = useState(false);
  // TODO(MUS-278): temporary Firefox block while bugs are fixed.
  const [showFirefoxBlockingScreen, setShowFirefoxBlockingScreen] = useState(false);
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const explicitPreferredInputMode = useExplicitPreferredInputMode();

  // Don't show blocking screens while auth is still loading or user data hasn't been fetched yet
  const authInProgress =
    auth.isLoading ||
    auth.oauthPending ||
    auth.processPending ||
    (auth.isAuthenticated && !userDataFetched);

  useEffect(() => {
    if (isAuthOrOnboardingPath(pathname) || authInProgress) {
      setShowMobileBlockingScreen(false);
      setShowSafariBlockingScreen(false);
      // TODO(MUS-278): temporary Firefox block while bugs are fixed.
      setShowFirefoxBlockingScreen(false);
      return;
    }

    // MUS-448 - whole-app hard block for phone/tablet web. Play requires Web MIDI,
    // which WebKit (all iOS/iPad browsers) lacks and which is unavailable to mobile
    // users generally; prod is MIDI-only, so play cannot run on any non-desktop web
    // surface. Independent of the desktop Safari/Firefox blockers below.
    setShowMobileBlockingScreen(
      shouldShowMobileWebBlocker({
        deviceClass: getDeviceClass(),
        isNative: isCapacitorNative(),
        pathname,
      }),
    );

    // Check paths that allow on any browser
    if (!ALLOWED_PATHS_ANY_PLATFORM.includes(pathname)) {
      console.log(
        'isSafariBrowser()',
        isSafariBrowser(),
        "&& !pathname.includes('verify')",
        !pathname.includes('verify'),
      );
      const shouldBlockSafariWeb =
        !isCapacitorNative() &&
        isSafariBrowser() &&
        detectDevice() == 'web' &&
        shouldShowSafariCompatibilityBlocker(audioRecognitionEnabled, explicitPreferredInputMode) &&
        !pathname.includes('verify');

      if (shouldBlockSafariWeb) {
        // return <SafariBlockingScreen />;
        setShowSafariBlockingScreen(true);
        console.log('safari browser detected, redirecting to roadmap');
        // navigate('/roadmap')
      } else {
        setShowSafariBlockingScreen(false);
      }

      // TODO(MUS-278): temporary Firefox block while bugs are fixed.
      // Mirrors the Safari block above; remove this whole `if` (and the related
      // Firefox state/effect/modal branch) once Firefox is supported again.
      if (
        !isCapacitorNative() &&
        isFirefoxBrowser() &&
        detectDevice() == 'web' &&
        !pathname.includes('verify')
      ) {
        setShowFirefoxBlockingScreen(true);
        console.log('firefox browser detected, showing blocking screen');
      } else {
        setShowFirefoxBlockingScreen(false);
      }
    } else {
      setShowMobileBlockingScreen(false);
      setShowSafariBlockingScreen(false);
      // TODO(MUS-278): temporary Firefox block while bugs are fixed.
      setShowFirefoxBlockingScreen(false);
    }
  }, [pathname, authInProgress, audioRecognitionEnabled, explicitPreferredInputMode]);

  useEffect(() => {
    if (showSafariBlockingScreen) {
      dispatch(eventActions.safariBlockedEventAction());
    }
  }, [dispatch, showSafariBlockingScreen]);

  // TODO(MUS-278): temporary Firefox block while bugs are fixed.
  useEffect(() => {
    if (showFirefoxBlockingScreen) {
      dispatch(eventActions.firefoxBlockedEventAction());
    }
  }, [dispatch, showFirefoxBlockingScreen]);

  // None of the blockers can be closed. They are not shown in the store apps today; if one ever is,
  // Android's Back sends the app to the background instead of reaching the screen beneath.
  useBlockingGateHoldsBack(
    showSafariBlockingScreen || showMobileBlockingScreen || showFirefoxBlockingScreen,
  );

  return (
    <>
      {showSafariBlockingScreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.80)',
            zIndex: DEVICE_BLOCKER_Z_INDEX,
          }}
        >
          <CompatibilityBlockingModal blockingScreen="safari" />
          <Box
            sx={{
              position: 'absolute',
              bottom: 'calc(8vh + env(safe-area-inset-bottom, 0px))',
              left: '20px',
            }}
          >
            <LogOutButton sx={{}} />
          </Box>
        </Box>
      )}

      {showMobileBlockingScreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.80)',
            zIndex: DEVICE_BLOCKER_Z_INDEX,
          }}
        >
          <CompatibilityBlockingModal
            blockingScreen="mobile"
            storeTarget={storeTargetForDevice(detectDevice())}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 'calc(8vh + env(safe-area-inset-bottom, 0px))',
              left: '20px',
            }}
          >
            <LogOutButton sx={{}} />
          </Box>
        </Box>
      )}

      {/* TODO(MUS-278): temporary Firefox block while bugs are fixed. Remove this block once Firefox is supported. */}
      {showFirefoxBlockingScreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.80)',
            zIndex: DEVICE_BLOCKER_Z_INDEX,
          }}
        >
          <CompatibilityBlockingModal blockingScreen="firefox" />
          <Box
            sx={{
              position: 'absolute',
              bottom: 'calc(8vh + env(safe-area-inset-bottom, 0px))',
              left: '20px',
            }}
          >
            <LogOutButton sx={{}} />
          </Box>
        </Box>
      )}
    </>
  );
};

interface BlockingModalProps {
  blockingScreen: string;
  storeTarget?: StoreTarget | null;
}

const CompatibilityBlockingModal: FC<BlockingModalProps> = ({ blockingScreen, storeTarget }) => {
  // MUS-427 fold-in: tap tries to open the installed app first (museflow://), then
  // falls back to the store. Keeping the store URL on the anchor's href preserves a
  // no-JS / long-press fallback; onClick handles the deep-link-first path.
  const handleGetApp = (storeUrl: string) => (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    openAppThenStore(storeUrl, {
      navigate: (url) => {
        window.location.href = url;
      },
      isVisible: () => !document.hidden,
      schedule: (fn, ms) => {
        window.setTimeout(fn, ms);
      },
    });
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            textAlign: 'center',
          }}
        >
          {blockingScreen === 'safari' && (
            <>
              <Box>
                <Logo absolute={false} href="https://www.museflow.ai" gradient={false} />
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    lineHeight: '145%',
                    color: MUSEFLOW_OFF_WHITE_1,
                    maxWidth: '400px',
                    marginTop: '-40px',
                    px: 2,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  }}
                >
                  To continue on the web, use Chrome or Edge
                </Box>
              </Box>
            </>
          )}

          {/* TODO(MUS-278): temporary Firefox block while bugs are fixed. Remove this branch once Firefox is supported. */}
          {blockingScreen === 'firefox' && (
            <>
              <Box>
                <Logo absolute={false} href="https://www.museflow.ai" gradient={false} />
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    lineHeight: '145%',
                    color: MUSEFLOW_OFF_WHITE_1,
                    maxWidth: '400px',
                    marginTop: '-40px',
                    px: 2,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  }}
                >
                  Firefox isn&apos;t fully supported yet. To continue on the web, use Chrome or Edge
                </Box>
              </Box>
            </>
          )}

          {blockingScreen === 'mobile' && (
            <>
              <Box>
                <Logo absolute={false} href="museflow://" gradient={false} />
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: MUSEFLOW_OFF_WHITE_1,
                    maxWidth: '400px',
                    marginTop: '-40px',
                    px: 2,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  }}
                >
                  MuseFlow&apos;s playing experience isn&apos;t available in a mobile browser. Get
                  the app to play, or use a computer with Chrome or Edge.
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginTop: '20px',
                  }}
                >
                  {(storeTarget === 'ios' || storeTarget == null) && (
                    <Box
                      component="a"
                      href={IOS_APP_STORE_URL}
                      onClick={handleGetApp(storeUrlForTarget('ios') ?? IOS_APP_STORE_URL)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-block',
                        px: '20px',
                        py: '10px',
                        borderRadius: '10px',
                        backgroundColor: '#4A90D9',
                        color: '#FFFFFF',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        '&:hover': { opacity: 0.85 },
                      }}
                    >
                      Download on the App Store
                    </Box>
                  )}
                  {(storeTarget === 'android' || storeTarget == null) && (
                    <Box
                      component="a"
                      href={ANDROID_PLAY_STORE_URL}
                      onClick={handleGetApp(storeUrlForTarget('android') ?? ANDROID_PLAY_STORE_URL)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-block',
                        px: '20px',
                        py: '10px',
                        borderRadius: '10px',
                        backgroundColor: '#4A90D9',
                        color: '#FFFFFF',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        '&:hover': { opacity: 0.85 },
                      }}
                    >
                      Get it on Google Play
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export { DeviceCompatibilityBlockingScreen };
